// entities
import {
  ExamAnalysisProps,
  ExamMarkerValues,
  exam_analysis_category,
  exam_analysis_trimester,
  exam_analysis_children_range,
  marker_status
} from '@/entities/exam-analysis'
import { MarkerDef, MarkerRange, MarkerRangeKey } from './markers/types'

// markers
import { GESTANTE_MARKERS } from './markers/gestante'
import { MULHER_MARKERS } from './markers/mulher'
import { HOMEM_MARKERS } from './markers/homem'
import { CRIANCA_MARKERS } from './markers/crianca'

export function getMarkersForCategory(category: exam_analysis_category): MarkerDef[] {
  switch (category) {
    case exam_analysis_category.GESTANTE:
      return GESTANTE_MARKERS
    case exam_analysis_category.TENTANTE:
    case exam_analysis_category.MULHER:
      return MULHER_MARKERS
    case exam_analysis_category.CRIANCA:
      return CRIANCA_MARKERS
    case exam_analysis_category.HOMEM:
      return [...HOMEM_MARKERS.espermograma, ...HOMEM_MARKERS.homem]
    default:
      return []
  }
}

export function getHomemSplitInfo(category: exam_analysis_category): {
  espermograma: MarkerDef[]
  homem: MarkerDef[]
} | null {
  if (category !== exam_analysis_category.HOMEM) return null
  return HOMEM_MARKERS
}

export function resolveRangeKey(record: Pick<ExamAnalysisProps, 'category' | 'trimester' | 'childrenRange'>): MarkerRangeKey {
  if (record.category === exam_analysis_category.GESTANTE) {
    if (record.trimester === exam_analysis_trimester.TRIM1) return 'TRIM1'
    if (record.trimester === exam_analysis_trimester.TRIM2) return 'TRIM2'
    if (record.trimester === exam_analysis_trimester.TRIM3) return 'TRIM3'
    return 'TRIM1'
  }
  if (record.category === exam_analysis_category.CRIANCA) {
    if (record.childrenRange === exam_analysis_children_range.R_0_6M) return '0-6m'
    if (record.childrenRange === exam_analysis_children_range.R_6M_2A) return '6m-2a'
    if (record.childrenRange === exam_analysis_children_range.R_2_10A) return '2-10a'
    if (record.childrenRange === exam_analysis_children_range.R_10_18A) return '10-18a'
    return '0-6m'
  }
  return 'default'
}

export function getMarkerRange(marker: MarkerDef, rangeKey: MarkerRangeKey): MarkerRange | undefined {
  return marker.ranges[rangeKey] ?? marker.ranges.default
}

export function parseNumber(input?: string): number {
  if (input === undefined || input === null) return NaN
  const s = String(input).trim().replace(',', '.').replace(/[^0-9.\-]/g, '')
  if (!s) return NaN
  const n = Number(s)
  return Number.isFinite(n) ? n : NaN
}

export function classifyMarker(value: string | undefined, range: MarkerRange | undefined): marker_status {
  if (value === undefined || value === null || String(value).trim() === '') return marker_status.EMPTY
  if (!range) return marker_status.EMPTY
  const n = parseNumber(value)
  if (!Number.isFinite(n)) return marker_status.EMPTY

  const { min, max } = range

  if (min !== undefined && max !== undefined) {
    const margin = Math.max((max - min) * 0.1, 0.0001)
    if (n >= min && n <= max) return marker_status.OK
    if ((n >= min - margin && n < min) || (n > max && n <= max + margin)) return marker_status.ATTENTION
    return n < min ? marker_status.BELOW : marker_status.ABOVE
  }
  if (min !== undefined) {
    const margin = Math.max(Math.abs(min) * 0.1, 0.0001)
    if (n >= min) return marker_status.OK
    if (n >= min - margin) return marker_status.ATTENTION
    return marker_status.BELOW
  }
  if (max !== undefined) {
    const margin = Math.max(Math.abs(max) * 0.1, 0.0001)
    if (n <= max) return marker_status.OK
    if (n <= max + margin) return marker_status.ATTENTION
    return marker_status.ABOVE
  }
  return marker_status.EMPTY
}

export const STATUS_LABEL: Record<marker_status, string> = {
  [marker_status.EMPTY]: '—',
  [marker_status.OK]: 'OK',
  [marker_status.ABOVE]: 'ACIMA',
  [marker_status.BELOW]: 'ABAIXO',
  [marker_status.ATTENTION]: 'ATENÇÃO'
}

export const STATUS_INPUT_CLASS: Record<marker_status, string> = {
  [marker_status.EMPTY]: '',
  [marker_status.OK]: 'border-emerald-500 bg-emerald-50 text-emerald-900 focus-visible:ring-emerald-300',
  [marker_status.ABOVE]: 'border-red-500 bg-red-50 text-red-900 focus-visible:ring-red-300',
  [marker_status.BELOW]: 'border-red-500 bg-red-50 text-red-900 focus-visible:ring-red-300',
  [marker_status.ATTENTION]: 'border-amber-500 bg-amber-50 text-amber-900 focus-visible:ring-amber-300'
}

export const STATUS_BADGE_CLASS: Record<marker_status, string> = {
  [marker_status.EMPTY]: 'bg-muted text-muted-foreground',
  [marker_status.OK]: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
  [marker_status.ABOVE]: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  [marker_status.BELOW]: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  [marker_status.ATTENTION]: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'
}

export interface MarkerStats {
  filled: number
  ok: number
  attention: number
  outOfRange: number
  total: number
}

/**
 * Calcula stats considerando a coleta indicada por `collectionRef`.
 * Quando `collectionRef` é omitido, considera o "estado consolidado":
 *   - "preenchido" = qualquer coleta com valor
 *   - status = pior status entre as coletas
 */
export function computeMarkerStats(
  values: ExamMarkerValues,
  markers: MarkerDef[],
  rangeKey: MarkerRangeKey,
  collectionRef?: string
): MarkerStats {
  let filled = 0
  let ok = 0
  let attention = 0
  let outOfRange = 0
  for (const m of markers) {
    const byCol = values[m.id] || {}
    const range = getMarkerRange(m, rangeKey)

    if (collectionRef) {
      const v = byCol[collectionRef]
      if (v === undefined || v === null || String(v).trim() === '') continue
      filled++
      const status = classifyMarker(v, range)
      if (status === marker_status.OK) ok++
      else if (status === marker_status.ATTENTION) attention++
      else if (status === marker_status.ABOVE || status === marker_status.BELOW) outOfRange++
    } else {
      const vals = Object.values(byCol).filter(v => v !== undefined && v !== null && String(v).trim() !== '')
      if (vals.length === 0) continue
      filled++
      // pior status entre as coletas — prioriza fora > atenção > ok
      const statuses = vals.map(v => classifyMarker(v, range))
      if (statuses.some(s => s === marker_status.ABOVE || s === marker_status.BELOW)) outOfRange++
      else if (statuses.some(s => s === marker_status.ATTENTION)) attention++
      else if (statuses.some(s => s === marker_status.OK)) ok++
    }
  }
  return { filled, ok, attention, outOfRange, total: markers.length }
}

export function groupMarkers(markers: MarkerDef[]): { group: string; items: MarkerDef[] }[] {
  const result: { group: string; items: MarkerDef[] }[] = []
  const map = new Map<string, MarkerDef[]>()
  for (const m of markers) {
    if (!map.has(m.group)) {
      const arr: MarkerDef[] = []
      map.set(m.group, arr)
      result.push({ group: m.group, items: arr })
    }
    map.get(m.group)!.push(m)
  }
  return result
}

export function findMarker(markers: MarkerDef[], id: string): MarkerDef | undefined {
  return markers.find(m => m.id === id)
}

/**
 * Constrói o `markersContext` que o frontend envia ao backend para enriquecer
 * o prompt da IA. Saída:
 *
 *   { [markerId]: {
 *       name, group, unit?,
 *       range: { display?, min?, max? },
 *       byCollection: { [colRef]: { value, status } }
 *     }
 *   }
 *
 * IDs órfãos (sem entry em `markersList`) NÃO entram aqui — o backend trata
 * via fallback como "marcador legado" sem classificação.
 */
export function buildMarkersContext(
  markers: MarkerDef[],
  rangeKey: MarkerRangeKey,
  values: ExamMarkerValues
): Record<string, {
  name: string
  group?: string
  unit?: string
  range?: { display?: string; min?: number; max?: number }
  byCollection?: Record<string, { value: string; status?: string }>
}> {
  const out: Record<string, { name: string; group?: string; unit?: string; range?: { display?: string; min?: number; max?: number }; byCollection?: Record<string, { value: string; status?: string }> }> = {}
  for (const m of markers) {
    const byCol = values[m.id] || {}
    const hasAnyValue = Object.values(byCol).some(v => v !== undefined && v !== null && String(v).trim() !== '')
    if (!hasAnyValue) continue
    const range = getMarkerRange(m, rangeKey)
    const byCollection: Record<string, { value: string; status?: string }> = {}
    for (const [colRef, v] of Object.entries(byCol)) {
      if (v === undefined || v === null || String(v).trim() === '') continue
      const status = classifyMarker(v, range)
      byCollection[colRef] = { value: v, status }
    }
    out[m.id] = {
      name: m.name,
      group: m.group,
      unit: range?.unit,
      range: range ? { display: range.display, min: range.min, max: range.max } : undefined,
      byCollection
    }
  }
  return out
}

/**
 * Forward-compat: separa IDs presentes em `markerValues` em:
 * - `known`: ids que ainda existem na lista atual de marcadores
 * - `orphans`: ids que NÃO estão na lista atual mas têm ao menos um valor preenchido
 *   (legado — preserva histórico em registros antigos do banco)
 *
 * Marcadores órfãos são mantidos no JSON para não perder dados, mas a UI os
 * exibe num bloco separado, sem classificação OK/ATENÇÃO/etc (não há range).
 */
export function partitionMarkerIds(
  values: ExamMarkerValues,
  knownMarkers: MarkerDef[]
): { known: string[]; orphans: string[] } {
  const knownIds = new Set(knownMarkers.map(m => m.id))
  const known: string[] = []
  const orphans: string[] = []
  for (const [id, byCol] of Object.entries(values || {})) {
    const hasAnyValue = Object.values(byCol || {}).some(v => v !== undefined && v !== null && String(v).trim() !== '')
    if (!hasAnyValue) continue
    if (knownIds.has(id)) known.push(id)
    else orphans.push(id)
  }
  return { known, orphans }
}
